// SF3 — Sessions to Orders Ratio.
//
// Its own component, never merged into the product table. The funnel answers
// where the catalogue as a whole loses shoppers; the table answers which
// product does. Built on daily account-level traffic, so this is the one
// surface here that the window control governs.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../ProductPerformance.module.css';
import { ClippedWindow } from '../../Sales/components/format';
import { getFunnel } from '../services/productPerformanceApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const count = (value) =>
  value === null || value === undefined ? '—' : inr.format(value);

const percent = (ratio, digits = 2) =>
  ratio === null || ratio === undefined ? '—' : `${(ratio * 100).toFixed(digits)}%`;

const STAGE_LABEL = {
  sessions: 'sessions', order_items: 'order items', units: 'units',
};

const formatSyncedAt = (iso) => {
  if (!iso) return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

// A step between two stages. Units can exceed order items, because one order
// item may carry several units, so a rise is labelled as one rather than shown
// as a negative loss.
const Step = ({ step }) => {
  const drop = step.kind === 'drop';
  return (
    <div className={drop ? local.stepDrop : local.stepRise}>
      {drop
        ? <>Lost <strong>{count(step.lost)}</strong> between {STAGE_LABEL[step.from]} and{' '}
          {STAGE_LABEL[step.to]}, a drop of <strong>{percent(step.rate ? -step.rate : null, 1)}</strong>.</>
        : <>Rose <strong>{count(step.change)}</strong> between {STAGE_LABEL[step.from]} and{' '}
          {STAGE_LABEL[step.to]}, because one order item can carry several units.</>}
    </div>
  );
};

const SessionsFunnel = ({ window: reportingWindow, onLoadingChange, onData }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getFunnel(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Shared with the page's KPI strip, so the same figures are not fetched twice.
  useEffect(() => { onData?.(data); }, [data, onData]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const stages = data?.funnel?.stages || [];
  const widest = stages.length ? Math.max(...stages.map((s) => s.value || 0)) : 0;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Sessions to orders</h2>
        {data?.days_with_data > 0 && (
          <span className={local.muted}>{data.days_with_data} days with data</span>
        )}
      </div>
      <p className={local.muted}>
        Where shoppers drop out between arriving at a listing and ordering,
        across the whole catalogue.
      </p>
      <ClippedWindow data={data} />

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the funnel'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        loading && !data ? (
          <div className={local.emptyCell}>Loading…</div>
        ) : (
          <>
            <div className={local.funnel}>
              {stages.map((stage) => (
                <div key={stage.key} className={local.stage}>
                  <div className={local.stageLabel}>{stage.label}</div>
                  <div className={local.stageBarWrap}>
                    <div
                      className={local.stageBar}
                      style={{ width: widest ? `${Math.max((stage.value / widest) * 100, 0.4)}%` : '0%' }}
                    />
                  </div>
                  <div className={local.stageValue}>{count(stage.value)}</div>
                </div>
              ))}
            </div>

            {/* A step between each stage, then the largest drop called out
                separately: it is the one place on this surface a seller acts. */}
            <div className={local.steps}>
              {(data?.funnel?.steps || []).map((step) => (
                <Step key={`${step.from}-${step.to}`} step={step} />
              ))}
            </div>

            {data?.funnel?.largest_drop && (
              <div className={local.dropOff}>
                Largest single drop:{' '}
                <strong>{count(data.funnel.largest_drop.lost)}</strong> lost
                between {STAGE_LABEL[data.funnel.largest_drop.from]} and{' '}
                {STAGE_LABEL[data.funnel.largest_drop.to]}.
              </div>
            )}

            <div className={local.ratios}>
              <div>
                <div className={local.ratioLabel}>Sessions to orders</div>
                <div className={local.ratioValue}>
                  {percent(data?.funnel?.sessions_to_orders)}
                </div>
              </div>
              <div>
                <div className={local.ratioLabel}>Units per order item</div>
                <div className={local.ratioValue}>
                  {data?.funnel?.units_per_order?.toFixed(2) ?? '—'}
                </div>
              </div>
              <div>
                <div className={local.ratioLabel}>Page views</div>
                <div className={local.ratioValue}>{count(data?.page_views)}</div>
              </div>
            </div>
          </>
        )
      )}

      {data?.freshness && (
        <div className={local.freshness}>
          Sessions come from Amazon's Sales and Traffic report, synced{' '}
          {formatSyncedAt(data.freshness.traffic_synced_at)}. That report trails
          live trading by about {data.freshness.report_lag_days} days, so the
          most recent days keep filling in.
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SessionsFunnel;
